import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import type { QueuedMutation } from './types';

const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];

// Endpoints that should never be queued for offline replay.
// System/background calls that are not user-initiated mutations.
const SKIP_QUEUE_PATTERNS = [
  '/auth/login',
  '/auth/logout',
  '/auth/register',
  '/auth/refresh',
  '/task-anticipation/',
  '/migration/gp/',
  '/offline-sync/',
  '/health',
  '/ws',
];

export class OfflineQueuedError extends Error {
  isOfflineQueued = true as const;
  constructor(message = 'Request queued for offline sync') {
    super(message);
    this.name = 'OfflineQueuedError';
  }
}

type AddMutationFn = (
  mutation: Omit<QueuedMutation, 'id' | 'queuedAt' | 'retries' | 'maxRetries'>,
) => Promise<void>;

function shouldSkipQueue(config: InternalAxiosRequestConfig): boolean {
  const url = config.url ?? '';
  return SKIP_QUEUE_PATTERNS.some((p) => url.includes(p));
}

function buildFullUrl(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL ?? '';
  const url = config.url ?? '';
  if (url.startsWith('http')) return url;

  // Build the path (avoid double-prefix)
  let path: string;
  if (base && url.startsWith(base)) {
    path = url;
  } else if (base.startsWith('http')) {
    return `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  } else {
    path = `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  }

  // Always produce an absolute URL so fetch() works correctly regardless
  // of environment (avoids file:// resolution in dev, works in production)
  if (typeof window !== 'undefined' && !path.startsWith('http')) {
    return `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`;
  }
  return path;
}

function extractHeaders(config: InternalAxiosRequestConfig): Record<string, string> | undefined {
  const headers: Record<string, string> = {};
  if (config.headers) {
    for (const [key, value] of Object.entries(config.headers)) {
      if (typeof value === 'string' && key.toLowerCase() !== 'content-type') {
        headers[key] = value;
      }
    }
  }
  return Object.keys(headers).length > 0 ? headers : undefined;
}

export function setupOfflineInterceptor(
  api: AxiosInstance,
  addMutation: AddMutationFn,
): () => void {
  const requestId = api.interceptors.request.use(
    async (config) => {
      const method = (config.method ?? 'get').toLowerCase();
      if (!MUTATING_METHODS.includes(method) || navigator.onLine || shouldSkipQueue(config)) {
        return config;
      }

      // Offline + mutating method → queue it
      await addMutation({
        method: method.toUpperCase() as QueuedMutation['method'],
        url: buildFullUrl(config),
        data: config.data,
        headers: extractHeaders(config),
      });

      return Promise.reject(new OfflineQueuedError());
    },
  );

  const responseId = api.interceptors.response.use(
    undefined,
    async (error: AxiosError) => {
      // Network error (no response) on mutating method → queue it
      if (
        !error.response &&
        error.config &&
        MUTATING_METHODS.includes((error.config.method ?? '').toLowerCase()) &&
        !shouldSkipQueue(error.config)
      ) {
        const method = error.config.method!.toUpperCase() as QueuedMutation['method'];
        await addMutation({
          method,
          url: buildFullUrl(error.config),
          data: error.config.data,
          headers: extractHeaders(error.config),
        });

        return Promise.reject(new OfflineQueuedError());
      }

      return Promise.reject(error);
    },
  );

  // Return cleanup function to eject interceptors
  return () => {
    api.interceptors.request.eject(requestId);
    api.interceptors.response.eject(responseId);
  };
}
